/**
 * CirclePattern - A decorative circular pattern with concentric circles
 * and radial gradient effect.
 *
 * @example
 * ```tsx
 * <div className="relative">
 *   <CirclePattern />
 *   <div className="relative z-10">Your content here</div>
 * </div>
 * ```
 */
import { useId } from 'react';

/**
 * @fileoverview Decorative background patterns for the TRM UI Kit.
 *
 * Contains reusable SVG patterns and decorative elements that can be used
 * as background elements throughout the application.
 */

interface CirclePatternProps {
  className?: string;
}

export const CirclePattern = ({ className = '' }: CirclePatternProps) => {
  const maskId = useId();
  const gradientId = useId();

  return (
    <svg
      width="336"
      height="336"
      viewBox="0 0 336 336"
      fill="none"
      className={`pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-border-secondary ${className}`}
    >
      <mask
        id={maskId}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="336"
        height="336"
        style={{ maskType: 'alpha' }}
      >
        <rect width="336" height="336" fill={`url(#${gradientId})`} />
      </mask>
      <g mask={`url(#${maskId})`}>
        <circle cx="168" cy="168" r="47.5" stroke="currentColor" strokeWidth="1" fill="none" />
        <circle cx="168" cy="168" r="71.5" stroke="currentColor" strokeWidth="1" fill="none" />
        <circle cx="168" cy="168" r="95.5" stroke="currentColor" strokeWidth="1" fill="none" />
        <circle cx="168" cy="168" r="119.5" stroke="currentColor" strokeWidth="1" fill="none" />
        <circle cx="168" cy="168" r="143.5" stroke="currentColor" strokeWidth="1" fill="none" />
        <circle cx="168" cy="168" r="167.5" stroke="currentColor" strokeWidth="1" fill="none" />
      </g>
      <defs>
        <radialGradient
          id={gradientId}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(168 168) rotate(90) scale(168 168)"
        >
          <stop />
          <stop offset="1" stopOpacity="0" />
        </radialGradient>
      </defs>
    </svg>
  );
};
